import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReservationSlotSummary } from '../../../../domain/reservationSlots';
import type { ReservationSlotRepository } from '../../../../application/reservationSlots/ports/ReservationSlotRepository';
import { getSummaries } from '../../../../application/reservationSlots';

/**
 * 解放枠一覧の1ページあたりの表示件数
 *
 * @description
 * サマリはAPIから全件取得するため、ページングはクライアントサイドで行う。
 * 1年分（12ヶ月）が1ページに収まる件数として12件を採用する。
 */
export const SUMMARY_ROWS_PER_PAGE = 12;

/**
 * useReservationSlotSummary フックの戻り値型
 *
 * @property {ReservationSlotSummary[]} summaries - 取得済みの全サマリ
 * @property {ReservationSlotSummary[]} pagedSummaries - 現在ページに表示するサマリ
 * @property {number} page - 現在のページ番号（0始まり）
 * @property {number} rowsPerPage - 1ページあたりの表示件数
 * @property {number} totalCount - 全サマリの件数
 * @property {function} setPage - ページ番号を変更する
 * @property {boolean} isLoading - ローディング状態
 * @property {string|null} error - エラーメッセージ
 * @property {function} reload - サマリを再取得する
 * @property {function} clearError - エラーメッセージをクリアする
 */
export interface UseReservationSlotSummaryReturn {
  summaries: ReservationSlotSummary[];
  pagedSummaries: ReservationSlotSummary[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  clearError: () => void;
}

/**
 * 解放枠一覧タブ用カスタムフック
 *
 * @description
 * 月別の解放枠サマリ（総数・カテゴリ別枠数）を取得し、
 * クライアントサイドのページング状態を保持する。
 *
 * @param {ReservationSlotRepository} repository - DIされるRepository実装
 */
export const useReservationSlotSummary = (
  repository: ReservationSlotRepository
): UseReservationSlotSummaryReturn => {
  const [summaries, setSummaries] = useState<ReservationSlotSummary[]>([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getSummaries(repository);
      setSummaries(result);
      // 再取得時は先頭ページに戻し、存在しないページに取り残されないようにする
      setPage(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'サマリの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalCount = summaries.length;

  /**
   * 実在するページ番号に丸めたページ番号
   * 件数が減った場合でも空ページを表示しないようにする
   */
  const safePage = useMemo(() => {
    const lastPage = Math.max(0, Math.ceil(totalCount / SUMMARY_ROWS_PER_PAGE) - 1);
    return Math.min(page, lastPage);
  }, [page, totalCount]);

  // 丸め結果をstateにも反映し、表示とstateの乖離を残さない
  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const pagedSummaries = useMemo(() => {
    const start = safePage * SUMMARY_ROWS_PER_PAGE;
    return summaries.slice(start, start + SUMMARY_ROWS_PER_PAGE);
  }, [summaries, safePage]);

  const clearError = useCallback(() => setError(null), []);

  return {
    summaries,
    pagedSummaries,
    page: safePage,
    rowsPerPage: SUMMARY_ROWS_PER_PAGE,
    totalCount,
    setPage,
    isLoading,
    error,
    reload,
    clearError,
  };
};
